import Select from "react-select";
import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import UserModal from "../../Components/UserModal";
import TransactionHistory from "../../Components/TransactionHistory";
import { FaTrashAlt, FaCheck, FaTimes } from "react-icons/fa";
import PromoteConfirmModal from "../../Components/PromoteConfirmModal";
import DeleteConfirmationModal from "../../Components/DeleteConfirmationModal";
import { toast } from "react-toastify";
import SpecialAgentModal from "../../Components/SpecialAgentModal";

const BASE_URL = process.env.REACT_APP_API_URL;

export default function Report() {
  const [activeTab, setActiveTab] = useState("agents");
  const [reportType, setReportType] = useState("agent"); // "agent" or "subagent"
  const [selectedReportUserId, setSelectedReportUserId] = useState("");
  const [selectedReportAgentId, setSelectedReportAgentId] = useState("");
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [reportData, setReportData] = useState(null);

  const [userRequests, setUserRequests] = useState([]);
  const [agents, setAgents] = useState([]);
  const [subagents, setSubagents] = useState([]);
  const [subagentsForReport, setSubagentsForReport] = useState([]);

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isCreatingSubagent, setIsCreatingSubagent] = useState(false);
  const [history, setHistory] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [currentUserRole, setCurrentUserRole] = useState();
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentTransactionPage, setCurrentTransactionPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToPromote, setUserToPromote] = useState(null);
  const [updatedUsers, setUpdatedUsers] = useState({});
  const [promoteLoading, setPromoteLoading] = useState(false);

  const [showSpecialAgentModal, setShowSpecialAgentModal] = useState(false);
  const [selectedSpecialAgent, setSelectedSpecialAgent] = useState(null);

  const [isCurrentUserSpecialAgent, setIsCurrentUserSpecialAgent] =
    useState(false);

  // Fetch subagents for selected agent in subagent report
  useEffect(() => {
    if (reportType === "subagent" && selectedReportAgentId) {
      const fetchSubagents = async () => {
        try {
          const response = await axios.get(
            `${BASE_URL}/users/get-all-sub-agents`,
            getAuthHeaders()
          );
          const filtered = (response.data || []).filter(
            (subagent) =>
              String(subagent.parentId) === String(selectedReportAgentId)
          );
          setSubagentsForReport(filtered);
        } catch (error) {
          setSubagentsForReport([]);
        }
      };
      fetchSubagents();
    } else {
      setSubagentsForReport([]);
    }
  }, [reportType, selectedReportAgentId]);

  // Fetch report for selected agent/subagent and date range
  const fetchReport = async () => {
    if (!selectedReportUserId || !reportStartDate || !reportEndDate) {
      toast.error("Please select user and date range.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const url =
        reportType === "agent"
          ? `${BASE_URL}/users/agent-report/${selectedReportUserId}`
          : `${BASE_URL}/users/subagent-report/${selectedReportUserId}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params: { startDate: reportStartDate, endDate: reportEndDate },
      });
      setReportData(response.data);
    } catch (error) {
      toast.error("Failed to fetch report");
      setReportData(null);
    }
  };
  useEffect(() => {
    const checkSpecialAgentStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        const user = JSON.parse(localStorage.getItem("user"));

        if (user?._id) {
          const response = await axios.get(
            `${BASE_URL}/special-agent/${user._id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          // Only mark true if API confirms special agent status explicitly
          const isSpecialAgent = Boolean(
            response.data?.agentData?.isSpecialAgent ||
              (Array.isArray(response.data?.agentData) &&
                response.data.agentData.length > 0)
          );

          setIsCurrentUserSpecialAgent(isSpecialAgent);
        }
      } catch (error) {
        console.error("Error checking special agent status:", error);
        setIsCurrentUserSpecialAgent(false);
      }
    };

    checkSpecialAgentStatus();
  }, []);

  const handleSpecialAgentUpgrade = (user) => {
    setSelectedSpecialAgent(user);
    setShowSpecialAgentModal(true);
  };
  // Download report as Excel
  const handleDownloadExcel = () => {
    if (
      !reportData ||
      !reportData.transactions ||
      reportData.transactions.length === 0
    ) {
      return;
    }
    // Get agent/subagent info
    let userInfo = null;
    if (reportType === "agent" && reportData.agent) {
      userInfo = reportData.agent;
    } else if (reportType === "subagent" && reportData.subagent) {
      userInfo = reportData.subagent;
    }
    const name = userInfo?.name || "";
    const entityId = userInfo?.entityId || "";

    // Heading rows
    const headingRows = [
      [reportType === "agent" ? "Agent Name" : "Subagent Name", name],
      ["Entity ID", entityId],
      ["Report Dates", `${reportStartDate} to ${reportEndDate}`],
      [], // Empty row for spacing
    ];

    // Data rows
    const dataRows = reportData.transactions.map((tx) => [
      tx.idNumber || "-",
      tx.documentType || "-",
      tx.amountDeducted || "N/A",
      tx.status || "-",
      tx.createdAt ? new Date(tx.createdAt).toLocaleString() : "-",
    ]);

    // Sheet header
    const tableHeader = [
      "Service Id",
      "Document Type",
      "Amount",
      "Status",
      "Date",
    ];

    // Combine all rows
    const sheetData = [...headingRows, tableHeader, ...dataRows];

    // Create worksheet and workbook
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    // Make heading rows bold
    const boldStyle = { font: { bold: true } };
    // Heading rows: 0, 1, 2 (and 4 for table header)
    [0, 1, 2].forEach((rowIdx) => {
      Object.keys(ws).forEach((cell) => {
        if (cell.startsWith("A") || cell.startsWith("B")) {
          const match = cell.match(/([A-Z]+)(\d+)/);
          if (match && Number(match[2]) - 1 === rowIdx) {
            ws[cell].s = boldStyle;
          }
        }
      });
    });
    // Table header row (row 4)
    Object.keys(ws).forEach((cell) => {
      const match = cell.match(/([A-Z]+)(\d+)/);
      if (match && Number(match[2]) - 1 === 4) {
        ws[cell].s = boldStyle;
      }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");

    // Format columns (optional, for better appearance)
    ws["!cols"] = [
      { wch: 18 }, // Service Id
      { wch: 22 }, // Document Type
      { wch: 10 }, // Amount
      { wch: 12 }, // Status
      { wch: 22 }, // Date
    ];

    // Filename: report_<startDate>_to_<endDate>_<agentname>.xlsx
    const safeName = name.replace(/[^a-zA-Z0-9]/g, "_");
    const filename = `report_${reportStartDate}_to_${reportEndDate}_${safeName}.xlsx`;
    XLSX.writeFile(wb, filename);
  };
  // Add this function to handle the actual promotion
  const handleConfirmSpecialAgent = async (formData) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${BASE_URL}/special-agent/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Update UI state
      setAgents((prevAgents) =>
        prevAgents.map((agent) =>
          agent._id === selectedSpecialAgent._id
            ? { ...agent, isSpecialAgent: true }
            : agent
        )
      );

      toast.success("Agent promoted to special agent successfully");
      setShowSpecialAgentModal(false);

      await fetchAllAgents();
    } catch (error) {
      console.error("Promotion failed:", error);
      toast.error(error.response?.data?.error || "Failed to promote agent");
    }
  };
  const handlePromoteClick = (user) => {
    setUserToPromote(user);
    setIsModalOpen(true);
  };

  const handleConfirmPromote = async (user) => {
    setPromoteLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${BASE_URL}/users/user/${user._id}/promote`,
        { email: user.email },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // ✅ 1. Remove from userRequests
      setUserRequests((prev) => prev.filter((u) => u._id !== user._id));

      // ✅ 2. Optionally add to agents
      setAgents((prev) => [...prev, response.data.user]);

      // ✅ 3. Close modal
      setIsModalOpen(false);
    } catch (error) {
      console.error("Promotion failed:", error);
      alert("Failed to promote user.");
    } finally {
      setPromoteLoading(false);
    }
  };

  const userRole = JSON.parse(
    localStorage.getItem("user")
  )?.role?.toLowerCase();

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (userId) => {
    try {
      // Call your DELETE API here
      await axios.delete(`${BASE_URL}/users/delete-agents/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success("User deleted successfully");

      // Refresh your list or filter out the deleted user
      setAgents((prev) => prev.filter((user) => user._id !== userId));

      setShowDeleteModal(false);
    } catch (error) {
      toast.error("Delete failed");
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, selectedAgentId]);

  useEffect(() => {
    setCurrentTransactionPage(1);
  }, [selectedUserId]);

  const getCurrentItems = (items) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  // Calculate total pages
  const getTotalPages = (items) => {
    return Math.ceil(items.length / itemsPerPage);
  };

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      const parsed = JSON.parse(userData);
      const role = parsed.role?.toLowerCase() || "admin";
      setCurrentUserRole(role);
    }
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const fetchAllUserRequests = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/users/get-all-user-requests`,
        getAuthHeaders()
      );
      setUserRequests(response.data || []);
    } catch (error) {
      console.error(error);
      alert("Failed to fetch user-requests.");
    }
  };

  const fetchAllAgents = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/users/get-all-agents`,
        getAuthHeaders()
      );
      setAgents(response.data || []);
      // If current user is agent, preselect their ID
      if (currentUserRole === "agent") {
        const currentUserId = localStorage.getItem("userId"); // You'll need to get this from auth
        setSelectedAgentId(currentUserId);
      }
    } catch (error) {
      console.error(error);
      alert("Failed to fetch agents.");
    }
  };

  const fetchSubagentsForAgent = async (agentId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/users/get-all-sub-agents`,
        getAuthHeaders()
      );
      // filter subagents based on parentId (string compare)
      const filteredSubagents = (response.data || []).filter(
        (subagent) => String(subagent.parentId) === String(agentId)
      );
      setSubagents(filteredSubagents);
    } catch (error) {
      console.error(error);
      alert("Failed to fetch subagents.");
    }
  };

  const fetchAllSubagents = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/users/get-all-sub-agents`,
        getAuthHeaders()
      );
      setSubagents(response.data.users || []);
    } catch (error) {
      console.error(error);
      alert("Failed to fetch subagents.");
    }
  };

  useEffect(() => {
    if (currentUserRole === "admin") {
      fetchAllAgents();
      fetchAllUserRequests();
      if (activeTab === "subagents") {
        fetchAllSubagents();
      }
    } else if (currentUserRole === "agent") {
      // For agents, only show subagents tab with their subagents
      setActiveTab("subagents");
      // Get current agent's _id from localStorage user
      const userData = localStorage.getItem("user");
      let currentUserId = null;
      if (userData) {
        try {
          const parsed = JSON.parse(userData);
          currentUserId = parsed._id;
        } catch {}
      }
      if (currentUserId) {
        fetchSubagentsForAgent(currentUserId);
      } else {
        setSubagents([]);
      }
    }
  }, [activeTab, currentUserRole]);

  // useEffect(() => {
  //   if (selectedUserId) {
  //     fetchUserHistory(selectedUserId);
  //   } else {
  //     setHistory([]);
  //   }
  // }, [selectedUserId]);

  let selectedUser = null;
  if (selectedUserId) {
    if (activeTab === "agents") {
      selectedUser = agents.find((u) => u?._id === selectedUserId) || null;
    } else if (activeTab === "subagents") {
      selectedUser = subagents.find((u) => u?._id === selectedUserId) || null;
    } else if (activeTab === "userRequests") {
      selectedUser =
        userRequests.find((u) => u?._id === selectedUserId) || null;
    }
  }
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      let response;

      if (modalData._id) {
        // Update existing user
        if (activeTab === "userRequests") {
          response = await axios.put(
            `${BASE_URL}/users/user/${modalData._id}`,
            {
              name: modalData.name,
              phone: modalData.phone,
              gender: modalData.gender,
              age: modalData.age,
              status: modalData.status,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } else {
          const endpoint =
            activeTab === "agents"
              ? `${BASE_URL}/users/update-agent/${modalData.entityId}`
              : `${BASE_URL}/users/update-sub-agent/${modalData.entityId}`;

          response = await axios.put(
            endpoint,
            {
              name: modalData.name,
              phone: modalData.phone,
              gender: modalData.gender,
              age: modalData.age,
              status: modalData.status,
              servicePrices: modalData.servicePrices || {},
              serviceAccess: modalData.serviceAccess || [],
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }

        // Update state based on active tab
        if (activeTab === "agents") {
          setAgents((prev) =>
            prev.map((agent) =>
              agent._id === modalData._id ? response.data.user : agent
            )
          );
        } else if (activeTab === "subagents") {
          setSubagents((prev) =>
            prev.map((subagent) =>
              subagent._id === modalData._id ? response.data.user : subagent
            )
          );
        } else if (activeTab === "userRequests") {
          setUserRequests((prev) =>
            prev.map((user) =>
              user._id === modalData._id ? response.data.user : user
            )
          );
        }

        // Keep the selectedUserId after update
        setSelectedUserId(modalData._id);

        toast.success("User updated successfully");
      } else {
        // Create new user
        if (isCreatingSubagent) {
          response = await axios.post(
            `${BASE_URL}/users/register-sub-agent`,
            {
              ...modalData,
              servicePrices: modalData.servicePrices || {},
              serviceAccess: modalData.serviceAccess || [],
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSubagents((prev) => [...prev, response.data.user]);
        } else {
          response = await axios.post(
            `${BASE_URL}/users/register-agent`,
            {
              ...modalData,
              servicePrices: modalData.servicePrices || {},
              serviceAccess: modalData.serviceAccess || [],
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setAgents((prev) => [...prev, response.data.user]);
        }
        toast.success("User created successfully");
      }

      setShowModal(false);
      setModalData(null);
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Error occurred during save"
      );
    }
  };
  const handleEdit = () => {
    if (!selectedUser) {
      alert("Please select a user to edit.");
      return;
    }
    setModalData({
      ...selectedUser,
      entityId: selectedUser.entityId,
      servicePrices: selectedUser.servicePrices || {},
      serviceAccess: selectedUser.serviceAccess || [],
    });
    setShowModal(true);
  };

  // Promote user request to sub-agent
  const handlePromote = async () => {
    if (!selectedUser) {
      alert("Please select a user to promote.");
      return;
    }
    const confirmed = window.confirm(
      `Promote ${selectedUser.name} to Subagent?`
    );
    if (!confirmed) return;
    try {
      // Call promote endpoint
      const response = await axios.put(
        `${BASE_URL}/users/user/${selectedUser._id}/promote`,
        {},
        getAuthHeaders()
      );
      alert(response.data.message || "User promoted successfully");
      await fetchAllUserRequests();
      setSelectedUserId(null);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to promote user.");
    }
  };
  const handleDeactivate = async () => {
    if (!selectedUser) {
      alert("Please select a user to deactivate.");
      return;
    }

    const confirmed = window.confirm(`Set ${selectedUser.name} to inactive?`);
    if (!confirmed) return;

    try {
      const isAgent = selectedUser.role === "agent";
      const endpoint = isAgent
        ? `${BASE_URL}/users/update-agent/${selectedUser.entityId}`
        : `${BASE_URL}/users/update-sub-agent/${selectedUser.entityId}`;

      await axios.put(endpoint, { status: "inactive" }, getAuthHeaders());

      alert("User deactivated successfully");

      if (activeTab === "agents") {
        await fetchAllAgents();
      } else {
        if (currentUserRole === "admin") {
          await fetchAllSubagents();
        } else {
          await fetchSubagentsForAgent(localStorage.getItem("userId"));
        }
      }

      setSelectedUserId(null);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to deactivate user.");
    }
  };
  const handleDeactivateUser = async () => {
    if (!selectedUser) {
      alert("Please select a user to deactivate.");
      return;
    }

    const confirmed = window.confirm(`Set ${selectedUser.name} to inactive?`);
    if (!confirmed) return;

    try {
      const isAgent = selectedUser.role === "agent";
      const endpoint = `${BASE_URL}/users/${selectedUser._id}/deactivate`;

      await axios.put(endpoint, { status: "inactive" }, getAuthHeaders());

      alert("User deactivated successfully");

      if (activeTab === "agents") {
        await fetchAllAgents();
        if (currentUserRole === "admin") {
          await fetchAllUserRequests();
        }
      } else if (activeTab === "subagents") {
        if (currentUserRole === "admin") {
          await fetchAllSubagents();
          await fetchAllUserRequests();
        } else {
          await fetchSubagentsForAgent(localStorage.getItem("userId"));
        }
      } else if (activeTab === "userRequests") {
        await fetchAllUserRequests();
      }

      setSelectedUserId(null);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to deactivate user.");
    }
  };
  const handleRestore = async () => {
    if (!selectedUser) {
      alert("Please select a user to restore.");
      return;
    }

    const confirmed = window.confirm(`Reactivate ${selectedUser.name}?`);
    if (!confirmed) return;

    try {
      const isAgent = selectedUser.role === "agent";
      const endpoint = isAgent
        ? `${BASE_URL}/users/update-agent/${selectedUser.entityId}`
        : `${BASE_URL}/users/update-sub-agent/${selectedUser.entityId}`;

      await axios.put(endpoint, { status: "active" }, getAuthHeaders());

      alert("User restored successfully");

      if (activeTab === "agents") {
        await fetchAllAgents();
      } else {
        if (currentUserRole === "admin") {
          await fetchAllSubagents();
        } else {
          await fetchSubagentsForAgent(localStorage.getItem("userId"));
        }
      }

      setSelectedUserId(null);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to restore user.");
    }
  };
  const handleCreateAgent = () => {
    setModalData({
      name: "",
      email: "",
      phone: "",
      password: "",
      gender: "",
      age: "",
      role: "agent", // role is fixed, do not render input for role
      status: "active",
    });
    setIsCreatingSubagent(false);
    setShowModal(true);
  };

  const handleCreateSubagent = () => {
    // For admin, they need to select an agent first
    if (currentUserRole === "admin" && !selectedAgentId) {
      alert("Please select an agent first to create a subagent under them.");
      return;
    }

    // For agent, their ID is automatically used
    const agentId =
      currentUserRole === "agent"
        ? localStorage.getItem("userId")
        : selectedAgentId;

    setModalData({
      name: "",
      email: "",
      phone: "",
      password: "",
      gender: "",
      age: "",
      role: "sub-agent", // role is fixed, do not render input for role
      status: "active",
      parentId: agentId,
    });
    setIsCreatingSubagent(true);
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!selectedUser) {
      alert("Please select a user to delete.");
      return;
    }

    const confirmed = window.confirm(`Set ${selectedUser.name} to inactive?`);
    if (!confirmed) return;

    try {
      // Determine the correct endpoint based on user role
      const isAgent = selectedUser.role === "agent";
      const endpoint = isAgent
        ? `${BASE_URL}/users/update-agent/${selectedUser.entityId}`
        : `${BASE_URL}/users/update-sub-agent/${selectedUser.entityId}`;

      // Update the user status to inactive
      await axios.put(endpoint, { status: "inactive" }, getAuthHeaders());

      alert("User status updated to inactive successfully");

      // Refresh data based on current tab
      if (activeTab === "agents") {
        await fetchAllAgents();
      } else {
        if (currentUserRole === "admin") {
          await fetchAllSubagents();
        } else {
          await fetchSubagentsForAgent(localStorage.getItem("userId"));
        }
      }

      setSelectedUserId(null);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to update user status.");
    }
  };

  const handleInputChange = (e) => {
    setModalData({ ...modalData, [e.target.name]: e.target.value });
  };

  const handleAgentSelect = (e) => {
    const agentId = e.target.value;
    setSelectedAgentId(agentId);
    if (agentId) {
      fetchSubagentsForAgent(agentId);
    } else {
      setSubagents([]);
    }
  };

  const fetchAgentTransactions = async (userId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/services/get-agent-transactions?userId=${userId}`,
        getAuthHeaders()
      );
      setTransactions(response.data.data || []);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      alert("Failed to fetch transactions");
    }
  };
  useEffect(() => {
    if (selectedUserId) {
      fetchAgentTransactions(selectedUserId);
    } else {
      setTransactions([]);
    }
  }, [selectedUserId]);

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        <h2 className="text-3xl text-center font-bold mb-4">Reports</h2>

        {/* Report selection UI */}
        <div className="bg-white p-4 rounded-xl shadow mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-sm font-medium mb-1">
                Report Type
              </label>
              <select
                value={reportType}
                onChange={(e) => {
                  setReportType(e.target.value);
                  setSelectedReportUserId("");
                  setSelectedReportAgentId("");
                  setReportData(null);
                }}
                className="border rounded p-2"
              >
                <option value="agent">Agent</option>
                <option value="subagent">Subagent</option>
              </select>
            </div>
            {reportType === "agent" && (
              <div className="min-w-[250px]">
                <label className="block text-sm font-medium mb-1">
                  Select Agent
                </label>
                <Select
                  options={agents.map((agent) => ({
                    value: agent._id,
                    label: `${agent.name} (${agent.email}${
                      agent.phone ? ", " + agent.phone : ""
                    })`,
                  }))}
                  value={
                    selectedReportUserId
                      ? agents
                          .filter((agent) => agent._id === selectedReportUserId)
                          .map((agent) => ({
                            value: agent._id,
                            label: `${agent.name} (${agent.email}${
                              agent.phone ? ", " + agent.phone : ""
                            })`,
                          }))[0]
                      : null
                  }
                  onChange={(option) =>
                    setSelectedReportUserId(option ? option.value : "")
                  }
                  isClearable
                  filterOption={(option, input) => {
                    const label = option.label.toLowerCase();
                    return label.includes(input.toLowerCase());
                  }}
                  placeholder="Search agent by name, email, or phone..."
                />
              </div>
            )}
            {reportType === "subagent" && (
              <>
                <div className="min-w-[250px]">
                  <label className="block text-sm font-medium mb-1">
                    Select Agent
                  </label>
                  <Select
                    options={agents.map((agent) => ({
                      value: agent._id,
                      label: `${agent.name} (${agent.email}${
                        agent.phone ? ", " + agent.phone : ""
                      })`,
                    }))}
                    value={
                      selectedReportAgentId
                        ? agents
                            .filter(
                              (agent) => agent._id === selectedReportAgentId
                            )
                            .map((agent) => ({
                              value: agent._id,
                              label: `${agent.name} (${agent.email}${
                                agent.phone ? ", " + agent.phone : ""
                              })`,
                            }))[0]
                        : null
                    }
                    onChange={(option) => {
                      setSelectedReportAgentId(option ? option.value : "");
                      setSelectedReportUserId("");
                    }}
                    isClearable
                    filterOption={(option, input) => {
                      const label = option.label.toLowerCase();
                      return label.includes(input.toLowerCase());
                    }}
                    placeholder="Search agent by name, email, or phone..."
                  />
                </div>
                <div className="min-w-[250px]">
                  <label className="block text-sm font-medium mb-1">
                    Select Subagent
                  </label>
                  <Select
                    options={subagentsForReport.map((subagent) => ({
                      value: subagent._id,
                      label: `${subagent.name} (${subagent.email}${
                        subagent.phone ? ", " + subagent.phone : ""
                      })`,
                    }))}
                    value={
                      selectedReportUserId
                        ? subagentsForReport
                            .filter(
                              (subagent) =>
                                subagent._id === selectedReportUserId
                            )
                            .map((subagent) => ({
                              value: subagent._id,
                              label: `${subagent.name} (${subagent.email}${
                                subagent.phone ? ", " + subagent.phone : ""
                              })`,
                            }))[0]
                        : null
                    }
                    onChange={(option) =>
                      setSelectedReportUserId(option ? option.value : "")
                    }
                    isClearable
                    isDisabled={!selectedReportAgentId}
                    filterOption={(option, input) => {
                      const label = option.label.toLowerCase();
                      return label.includes(input.toLowerCase());
                    }}
                    placeholder="Search subagent by name, email, or phone..."
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={reportStartDate}
                onChange={(e) => setReportStartDate(e.target.value)}
                className="border rounded p-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <input
                type="date"
                value={reportEndDate}
                onChange={(e) => setReportEndDate(e.target.value)}
                className="border rounded p-2"
              />
            </div>
            <button
              className="px-4 py-2 bg-purple-600 text-white rounded"
              onClick={fetchReport}
            >
              Get Report
            </button>
          </div>
        </div>

        {/* Report display */}
        {reportData && (
          <div className="bg-white p-4 rounded-xl shadow mb-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-xl font-semibold">Service Usage Report</h3>
              {reportData.transactions &&
                reportData.transactions.length > 0 && (
                  <button
                    className="px-3 py-2 bg-green-600 text-white rounded text-sm"
                    onClick={handleDownloadExcel}
                  >
                    Download Excel
                  </button>
                )}
            </div>
            {reportData.transactions && reportData.transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-100 font-semibold">
                    <tr>
                      <th className="p-2">Service Id</th>
                      <th className="p-2">Document Type</th>
                      <th className="p-2">Amount</th>
                      <th className="p-2">Status</th>
                      <th className="p-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.transactions.map((transaction) => (
                      <tr
                        key={transaction._id}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="p-2">{transaction.idNumber || "-"}</td>
                        <td className="p-2">
                          {transaction.documentType || "-"}
                        </td>
                        <td className="p-2">
                          {transaction.amountDeducted || "N/A"}
                        </td>
                        <td className="p-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              transaction.status === "success"
                                ? "bg-green-100 text-green-800"
                                : transaction.status === "pending" ||
                                  transaction.status === "failed"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {transaction.status === "success"
                              ? "success"
                              : "failed"}
                          </span>
                        </td>
                        <td className="p-2">
                          {new Date(transaction.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">
                No service usage found for this user.
              </p>
            )}
          </div>
        )}

        {/* ...existing code for user management, modals, etc. (optional) ... */}
      </div>
    </>
  );
}
